// apps/web/src/components/analytics/DecideTab.tsx
//
// P8a Decide tab — composes 5 panels and surfaces the prefs chip when
// the user has saved sector/country/vendor preferences. Spec §4.2 + §4.6.
//
// Prefs source: `useUserPreferences` (matching Dashboard's
// `useDashboardPrefsCounts` pattern). The plan referenced
// `useFilterDefaultsFromPrefs`, but that hook is generic over saved
// search shapes; the persistent user preferences live in
// `user_preferences.defaultIndustries / defaultRegions / trackedVendors`
// — same source the Dashboard chip already consumes.

import { useAuth } from '@/contexts/AuthContext'
import { useUserPreferences } from '@/hooks/queries'
import { PatchThisWeekPanel } from './decide/PatchThisWeekPanel'
import { PeerBenchmarkPanel } from './decide/PeerBenchmarkPanel'
import { TopVictimCountriesPanel } from './decide/TopVictimCountriesPanel'

export function DecideTab() {
  const { user } = useAuth()
  const userId = user?.sub ?? ''
  const prefsQuery = useUserPreferences(userId)
  const prefs = prefsQuery.data

  const sectors = prefs?.defaultIndustries ?? []
  const countries = prefs?.defaultRegions ?? []
  const vendors = prefs?.trackedVendors ?? []

  const hasPrefs = sectors.length + countries.length + vendors.length > 0

  return (
    <div className="flex flex-col gap-4">
      {hasPrefs && (
        <div
          data-testid="decide-prefs-chip"
          className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
        >
          Showing for: {sectors.length} sectors, {countries.length} countries
          {vendors.length > 0 && `, ${vendors.length} vendors`}
        </div>
      )}
      <section data-testid="decide-act-now" aria-labelledby="decide-act-now-h">
        <h2 id="decide-act-now-h" className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Act now</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <PatchThisWeekPanel />
          <TopVictimCountriesPanel />
        </div>
      </section>
      <section data-testid="decide-set-up" aria-labelledby="decide-set-up-h">
        <h2 id="decide-set-up-h" className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Set up</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <PeerBenchmarkPanel sectors={sectors} />
        </div>
      </section>
    </div>
  )
}
